import { useState } from "react";
import type { AjudaAntesDeAbrir } from "../../nucleo/ajudaAntesDeAbrir";
import { useArtigoDaAjuda } from "../../nucleo/conhecimento";
import { mensagemDoConsole } from "../../nucleo/console";
import { Aviso, Botao, Cartao } from "../ui";
import { Markdown } from "./Markdown";

function ArtigoAberto({
  artigoId,
  aoResolver,
  aoFechar,
}: {
  artigoId: string;
  aoResolver: () => void;
  aoFechar: () => void;
}) {
  const artigo = useArtigoDaAjuda(artigoId);
  if (artigo.isPending) return <p role="status">Abrindo o artigo…</p>;
  if (artigo.isError) return <Aviso tom="erro">{mensagemDoConsole(artigo.error)}</Aviso>;
  return (
    <div className="mt-3 border-t border-borda pt-3" data-testid="artigo-sugerido-aberto">
      <h4 className="titulo-secao">{artigo.data.titulo}</h4>
      <Markdown texto={artigo.data.corpo} />
      <div className="flex flex-wrap gap-2 mt-4">
        <Botao type="button" variante="verde" onClick={aoResolver}>
          Isso resolveu, não vou abrir o chamado
        </Botao>
        <Botao type="button" variante="fantasma" onClick={aoFechar}>
          Não resolveu, vou continuar
        </Botao>
      </div>
    </div>
  );
}

/**
 * Os artigos que podem resolver o problema, entre o assunto e o resto do formulário.
 *
 * O artigo abre AQUI, dentro do formulário: mandar a pessoa para outra página faria ela perder o
 * que já escreveu — e é a leitura completa que vale como "leu antes de abrir".
 */
export function SugestoesDeArtigos({
  ajuda,
  aoDesistir,
}: {
  ajuda: AjudaAntesDeAbrir;
  /** O formulário se esvazia: a pessoa decidiu não abrir o chamado. */
  aoDesistir: () => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);

  if (!ajuda.habilitada) return null;
  if (ajuda.resolvido) {
    return (
      <Aviso tom="sucesso" className="mb-4" data-testid="chamado-evitado">
        Que bom que o artigo resolveu. Não abrimos nenhum chamado — se o problema voltar, é só
        escrever de novo.
      </Aviso>
    );
  }
  if (ajuda.artigos.length === 0) return null;

  return (
    <Cartao
      titulo="Estes artigos podem resolver o seu problema"
      className="sugestoes-de-artigos"
      data-testid="sugestoes-de-artigos"
    >
      <ul className="lista-de-itens">
        {ajuda.artigos.map((artigo) => (
          <li key={artigo.id} data-testid="artigo-sugerido">
            <div className="flex items-start justify-between gap-3">
              <div>
                <strong>{artigo.titulo}</strong>
                {artigo.resumo ? <p className="cartao__texto mt-1">{artigo.resumo}</p> : null}
              </div>
              {aberto === artigo.id ? null : (
                <Botao
                  type="button"
                  variante="fantasma"
                  tamanho="pequeno"
                  aria-label={`Ler o artigo ${artigo.titulo}`}
                  onClick={() => {
                    ajuda.lerArtigo(artigo.id);
                    setAberto(artigo.id);
                  }}
                >
                  Ler aqui
                </Botao>
              )}
            </div>
            {aberto === artigo.id ? (
              <ArtigoAberto
                artigoId={artigo.id}
                aoFechar={() => setAberto(null)}
                aoResolver={() => {
                  ajuda.desistir(artigo.id);
                  setAberto(null);
                  aoDesistir();
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </Cartao>
  );
}
